import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Divider,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import SendIcon       from '@mui/icons-material/Send';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon      from '@mui/icons-material/Phone';
import EmailIcon      from '@mui/icons-material/Email';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import axios from 'axios';

const API_URL    = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const ADMIN_EMAIL = 'info@vitasecours.ca';

// ─── Animations ────────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Styled ────────────────────────────────────────────────────────────────────
const HeroSection = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(150deg, rgba(183,28,28,0.97) 0%, rgba(90,0,0,0.93) 100%)',
  color: '#fff',
  padding: theme.spacing(14, 0, 12),
  textAlign: 'center',
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -1, left: 0, right: 0,
    height: '70px',
    background: '#f7f8fc',
    clipPath: 'ellipse(60% 100% at 50% 100%)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-80px', right: '-80px',
    width: 340, height: 340,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.05)',
    pointerEvents: 'none',
  },
}));

const PageWrap = styled(Box)(({ theme }) => ({
  backgroundColor: '#f7f8fc',
  padding: theme.spacing(7, 0, 10),
}));

const Wrap = styled(Box)(({ theme }) => ({
  maxWidth: '1100px',
  margin: '0 auto',
  padding: theme.spacing(0, 3),
}));

const ContactInfoItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(0,0,0,0.05)',
  backgroundColor: '#fafafa',
  transition: 'all 0.25s ease',
  '&:hover': {
    borderColor: 'rgba(198,40,40,0.2)',
    backgroundColor: 'rgba(198,40,40,0.025)',
    transform: 'translateX(4px)',
  },
}));

// ─── Composant ─────────────────────────────────────────────────────────────────
const Contact = () => {
  const [formData, setFormData] = useState({ nom: '', prenom: '', email: '', telephone: '', message: '' });
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  // ── IA génération de message ──────────────────────────────────────────────
  const [aiOpen, setAiOpen] = useState(false);
  const [aiSujet, setAiSujet] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiFeaturesEnabled, setAiFeaturesEnabled] = useState(true);

  // Vérifier si les fonctionnalités IA sont activées
  useEffect(() => {
    const fetchAiStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/maintenance`);
        if (response.ok) {
          const data = await response.json();
          setAiFeaturesEnabled(data.aiFeaturesEnabled !== false);
        }
      } catch (error) {
        console.error('Erreur lors de la vérification du statut IA:', error);
      }
    };

    fetchAiStatus();
  }, []);

  const handleGenerateMessage = async () => {
    if (!aiSujet.trim()) return;
    setAiLoading(true);
    try {
      const res = await axios.post(`${API_URL}/ai/generate-contact-message`, {
        sujet: aiSujet,
        prenom: formData.prenom,
        nom: formData.nom,
      });
      if (res.data.success) {
        setFormData(prev => ({ ...prev, message: res.data.message }));
        setAiOpen(false);
        setAiSujet('');
      }
    } catch {
      // silencieux — l'utilisateur peut réessayer
    } finally {
      setAiLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.nom.trim())       errors.nom = 'Le nom est requis';
    if (!formData.prenom.trim())    errors.prenom = 'Le prénom est requis';
    if (!formData.email.trim())     errors.email = 'L\'email est requis';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Email invalide';
    if (!formData.telephone.trim()) errors.telephone = 'Le téléphone est requis';
    if (!formData.message.trim())   errors.message = 'Le message est requis';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = () => {
    setFormData({ nom: '', prenom: '', email: '', telephone: '', message: '' });
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      const htmlContent = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #c62828;">Nouveau message de contact</h2>
          <hr style="border: 1px solid #e0e0e0;" />
          <h3>Informations de l'expéditeur</h3>
          <table style="width: 100%; border-collapse: collapse;">
            <tr><td style="padding: 8px; background-color: #f5f5f5;"><strong>Nom complet</strong></td><td style="padding: 8px;">${formData.nom} ${formData.prenom}</td></tr>
            <tr><td style="padding: 8px; background-color: #f5f5f5;"><strong>Email</strong></td><td style="padding: 8px;">${formData.email}</td></tr>
            <tr><td style="padding: 8px; background-color: #f5f5f5;"><strong>Téléphone</strong></td><td style="padding: 8px;">${formData.telephone}</td></tr>
          </table>
          <h3>Message</h3>
          <div style="background-color: #f9f9f9; padding: 15px; border-left: 4px solid #c62828; border-radius: 4px;">
            ${formData.message.replace(/\n/g, '<br/>')}
          </div>
          <p style="margin-top: 30px; color: #777; font-size: 12px;">
            Ce message a été envoyé depuis le formulaire de contact du site Vitasecours.
          </p>
        </div>
      `;
      const textContent = `Nouveau message de contact\n-------------------------\nNom : ${formData.nom} ${formData.prenom}\nEmail : ${formData.email}\nTéléphone : ${formData.telephone}\n\nMessage :\n${formData.message}`;

      const response = await axios.post(`${API_URL}/mail/send`, {
        to: ADMIN_EMAIL,
        subject: `Nouveau message de contact - ${formData.nom} ${formData.prenom}`,
        html: htmlContent,
        text: textContent,
      });

      if (response.data.success) {
        // Notification admin (cloche + push) — discret, ne bloque pas l'envoi
        axios.post(`${API_URL}/notifications/contact`, {
          nom: `${formData.nom} ${formData.prenom}`.trim(),
          email: formData.email,
          sujet: formData.message?.slice(0, 80),
        }).catch((notifErr) => console.error('Notif contact (non bloquant):', notifErr));

        setSubmitStatus('success');
        resetForm();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setSubmitStatus('error');
        setErrorMessage(response.data.error || 'Erreur lors de l\'envoi');
      }
    } catch (err) {
      setSubmitStatus('error');
      setErrorMessage(err.response?.data?.error || 'Erreur de communication avec le serveur');
    } finally {
      setLoading(false);
    }
  };

  const INFO_ITEMS = [
    {
      icon: <PhoneIcon sx={{ fontSize: 22, color: '#c62828' }} />,
      label: 'Téléphone',
      value: '450 300-5005',
      sub: 'Lun–Ven, 9h–17h',
      href: 'tel:4503005005',
    },
    {
      icon: <EmailIcon sx={{ fontSize: 22, color: '#c62828' }} />,
      label: 'Courriel',
      value: 'info@vitasecours.ca',
      sub: 'Réponse sous 24h',
      href: 'mailto:info@vitasecours.ca',
    },
    {
      icon: <LocationOnIcon sx={{ fontSize: 22, color: '#c62828' }} />,
      label: 'Adresse',
      value: '275 Des Laurentides',
      sub: 'Saint-Jérôme, QC J7Z 7K8',
      href: null,
    },
    {
      icon: <AccessTimeIcon sx={{ fontSize: 22, color: '#c62828' }} />,
      label: 'Horaires',
      value: 'Lundi au Vendredi',
      sub: '9h00 – 17h00',
      href: null,
    },
  ];

  return (
    <>
      {/* ══════════════════ HERO ══════════════════ */}
      <HeroSection>
        <Box sx={{ position: 'absolute', bottom: '80px', left: '-50px', width: 220, height: 220, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />
        <Wrap sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ animation: `${fadeUp} 0.8s ease both` }}>
            <Typography variant="overline" sx={{ opacity: 0.75, letterSpacing: '0.18em', display: 'block', mb: 2 }}>
              On est là pour vous
            </Typography>
            <Typography variant="h1" sx={{ fontSize: { xs: '2.4rem', md: '3.6rem' }, fontWeight: 900, mb: 2.5, lineHeight: 1.1 }}>
              Contactez-nous
            </Typography>
            <Typography sx={{ maxWidth: '560px', mx: 'auto', fontSize: '1.1rem', lineHeight: 1.85, opacity: 0.88 }}>
              Une question sur nos formations ? Un projet de groupe ? Notre équipe répond rapidement.
            </Typography>
          </Box>
        </Wrap>
      </HeroSection>

      {/* ══════════════════ CORPS ══════════════════ */}
      <PageWrap>
        <Wrap>

          {/* Alertes */}
          {submitStatus === 'success' && (
            <Alert
              severity="success"
              icon={<CheckCircleIcon />}
              sx={{ mb: 4, borderRadius: '14px', fontSize: '1rem', alignItems: 'center' }}
              onClose={() => setSubmitStatus(null)}
            >
              Votre message a été envoyé avec succès. Nous vous répondrons dans les plus brefs délais.
            </Alert>
          )}
          {submitStatus === 'error' && (
            <Alert severity="error" sx={{ mb: 4, borderRadius: '14px' }} onClose={() => setSubmitStatus(null)}>
              {errorMessage || 'Une erreur est survenue. Veuillez réessayer.'}
            </Alert>
          )}

          <Grid container spacing={3} alignItems="stretch">

            {/* ── Infos de contact ── */}
            <Grid size={{ xs: 12, md: 5 }}>
              <Paper elevation={0} sx={{
                height: '100%',
                borderRadius: '24px',
                border: '1px solid rgba(0,0,0,0.07)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
                overflow: 'hidden',
              }}>
                {/* En-tête rouge */}
                <Box sx={{
                  background: 'linear-gradient(135deg, #c62828, #8e0000)',
                  p: 4, color: '#fff',
                }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.75 }}>
                    Informations de contact
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.85, lineHeight: 1.7 }}>
                    Plusieurs façons de nous joindre — choisissez celle qui vous convient le mieux.
                  </Typography>
                </Box>

                {/* Items */}
                <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {INFO_ITEMS.map((item, i) => (
                    <ContactInfoItem
                      key={i}
                      component={item.href ? 'a' : 'div'}
                      href={item.href || undefined}
                      sx={item.href ? { textDecoration: 'none', cursor: 'pointer' } : {}}
                    >
                      <Box sx={{
                        width: 44, height: 44,
                        borderRadius: '12px',
                        background: 'rgba(198,40,40,0.1)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        {item.icon}
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{
                          color: '#999', fontWeight: 700, display: 'block',
                          fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em',
                        }}>
                          {item.label}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700, color: '#1a1a1a', lineHeight: 1.3 }}>
                          {item.value}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#888' }}>
                          {item.sub}
                        </Typography>
                      </Box>
                    </ContactInfoItem>
                  ))}
                </Box>

                <Divider sx={{ mx: 3 }} />

                {/* Note bas de card */}
                <Box sx={{ p: 3, pt: 2 }}>
                  <Typography variant="caption" sx={{ color: '#aaa', fontStyle: 'italic', lineHeight: 1.7 }}>
                    Tous les champs sont obligatoires. Vos données sont traitées avec confidentialité et ne seront jamais partagées.
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* ── Formulaire ── */}
            <Grid size={{ xs: 12, md: 7 }}>
              <Paper elevation={0} sx={{
                borderRadius: '24px',
                border: '1px solid rgba(0,0,0,0.07)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
                overflow: 'hidden',
                height: '100%',
              }}>
                {/* En-tête formulaire */}
                <Box sx={{
                  borderBottom: '1px solid rgba(0,0,0,0.06)',
                  px: 4, py: 3,
                  display: 'flex', alignItems: 'center', gap: 2,
                }}>
                  <Box sx={{
                    width: 42, height: 42,
                    borderRadius: '12px',
                    background: 'rgba(198,40,40,0.1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#c62828',
                  }}>
                    <SendIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a', lineHeight: 1.2 }}>
                      Envoyez-nous un message
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#999' }}>
                      Réponse garantie sous 24h ouvrables
                    </Typography>
                  </Box>
                </Box>

                {/* Champs */}
                <Box
                  component="form"
                  onSubmit={handleSubmit}
                  sx={{ p: { xs: 3, md: 4 }, display: 'flex', flexDirection: 'column', gap: 2.5 }}
                >
                  {/* Nom + Prénom côte à côte */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
                    <TextField
                      required fullWidth label="Nom"
                      name="nom" value={formData.nom}
                      onChange={handleChange}
                      error={!!formErrors.nom} helperText={formErrors.nom}
                      disabled={loading}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                    />
                    <TextField
                      required fullWidth label="Prénom"
                      name="prenom" value={formData.prenom}
                      onChange={handleChange}
                      error={!!formErrors.prenom} helperText={formErrors.prenom}
                      disabled={loading}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                    />
                  </Box>

                  <TextField
                    required fullWidth label="Courriel" type="email"
                    name="email" value={formData.email}
                    onChange={handleChange}
                    error={!!formErrors.email} helperText={formErrors.email}
                    disabled={loading}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                  <TextField
                    required fullWidth label="Téléphone"
                    name="telephone" value={formData.telephone}
                    onChange={handleChange}
                    error={!!formErrors.telephone} helperText={formErrors.telephone}
                    disabled={loading}
                    placeholder="514 000-0000"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                  {/* Champ Message + bouton IA */}
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                      <Typography variant="caption" sx={{ color: '#666', fontWeight: 600 }}>
                        Message *
                      </Typography>
                      {aiFeaturesEnabled && (
                        <Button
                          size="small"
                          onClick={() => setAiOpen(true)}
                          sx={{
                            fontSize: '0.75rem', fontWeight: 700, textTransform: 'none',
                            color: '#7b2ff7', borderColor: '#7b2ff7', borderRadius: '20px',
                            px: 1.5, py: 0.4,
                            border: '1px solid',
                            '&:hover': { background: 'rgba(123,47,247,0.06)' },
                          }}
                        >
                          ✨ Générer avec l'IA
                        </Button>
                      )}
                    </Box>
                    <TextField
                      required fullWidth multiline rows={5}
                      name="message" value={formData.message}
                      onChange={handleChange}
                      error={!!formErrors.message} helperText={formErrors.message}
                      disabled={loading}
                      placeholder="Bonjour, j'aimerais obtenir plus d'informations sur..."
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                    />
                  </Box>

                  {/* Popup IA */}
                  {aiOpen && (
                    <Box sx={{
                      position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.5)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1400,
                    }}>
                      <Box sx={{
                        bgcolor: '#fff', borderRadius: 4, p: 4,
                        width: '100%', maxWidth: 480,
                        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                      }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                          ✨ Générer votre message
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#888', mb: 2.5 }}>
                          Décrivez brièvement ce que vous souhaitez communiquer — l'IA rédigera un message professionnel pour vous.
                        </Typography>
                        <TextField
                          fullWidth multiline rows={3}
                          value={aiSujet}
                          onChange={e => setAiSujet(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) handleGenerateMessage(); }}
                          placeholder="Ex : Je veux des informations sur les formations RCR pour mon équipe de 12 employés en janvier"
                          sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                          autoFocus
                        />
                        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                          <Button onClick={() => { setAiOpen(false); setAiSujet(''); }} sx={{ borderRadius: '20px', color: '#666' }}>
                            Annuler
                          </Button>
                          <Button
                            variant="contained"
                            onClick={handleGenerateMessage}
                            disabled={aiLoading || !aiSujet.trim()}
                            endIcon={aiLoading ? <CircularProgress size={16} color="inherit" /> : null}
                            sx={{
                              borderRadius: '20px', fontWeight: 700, textTransform: 'none',
                              background: 'linear-gradient(135deg, #7b2ff7, #c62828)',
                              '&:hover': { background: 'linear-gradient(135deg, #6a1fe0, #b71c1c)' },
                              '&.Mui-disabled': { background: '#e0e0e0' },
                            }}
                          >
                            {aiLoading ? 'Génération…' : 'Générer'}
                          </Button>
                        </Stack>
                      </Box>
                    </Box>
                  )}

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading}
                    endIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                    sx={{
                      background: 'linear-gradient(135deg, #c62828, #8e0000)',
                      py: 1.7, borderRadius: '50px',
                      fontWeight: 800, fontSize: '1rem',
                      textTransform: 'none',
                      boxShadow: '0 8px 24px rgba(198,40,40,0.28)',
                      '&:hover': { background: 'linear-gradient(135deg, #b71c1c, #7f0000)', transform: 'translateY(-2px)' },
                      '&.Mui-disabled': { background: '#e0e0e0' },
                    }}
                  >
                    {loading ? 'Envoi en cours…' : 'Envoyer le message'}
                  </Button>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* ── Bande de raccourcis rapides ── */}
          <Box sx={{
            mt: 4,
            p: { xs: 3, md: 4 },
            borderRadius: '20px',
            background: 'linear-gradient(135deg, #c62828, #8e0000)',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 3,
          }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#fff', mb: 0.5 }}>
                Vous préférez nous appeler directement ?
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Notre équipe vous répond pendant les heures d'ouverture.
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexShrink={0}>
              <Button
                href="tel:4503005005"
                variant="contained"
                startIcon={<PhoneIcon />}
                sx={{
                  background: '#fff', color: '#c62828', fontWeight: 700,
                  borderRadius: '50px', px: 3, py: 1.2,
                  whiteSpace: 'nowrap',
                  '&:hover': { background: 'rgba(255,255,255,0.9)' },
                }}
              >
                450 300-5005
              </Button>
              <Button
                component={Link} to="/emplacement"
                variant="outlined"
                startIcon={<LocationOnIcon />}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  borderColor: 'rgba(255,255,255,0.6)', color: '#fff', fontWeight: 700,
                  borderRadius: '50px', px: 3, py: 1.2,
                  whiteSpace: 'nowrap',
                  '&:hover': { borderColor: '#fff', background: 'rgba(255,255,255,0.1)' },
                }}
              >
                Voir l'emplacement
              </Button>
            </Stack>
          </Box>

        </Wrap>
      </PageWrap>
    </>
  );
};

export default Contact;
